/**
 * QMP-FE-8c — THE «<QWP> · parties» EDITOR OF v83, full screen
 * (docs/design/mockups/mockup-qmp-v83-official.html: `packageEditor`
 * l. 2028–2038, `saveFromForm('package')` l. 2196–2206; dark values of the
 * `#cqa-qmp-flow` tokens, the same ones `RuleEditorPage` draws with).
 *
 * «Save parties» is ONE act (D43): `PUT /api/qwps/{qwp_id}/structure` with the
 * three positions (null = «Not assigned» / «None») and the `structure_token`
 * the plan served in `GET /qmp` `structure[]`. The three old doors
 * (`updateQwpParties`, `appointQwpExecutingParty`, the `updateQwp` transfer)
 * are never called from here.
 *
 * «Not assigned» (the owner's one word, 04/11) is offered on the Executing
 * Party ONLY while it is not named: a named Executing Party never goes back.
 *
 * REFUSALS stay on the editor, the form untouched, in the server's words:
 *   · 409 `QWP_CHANGED` — the sentence and «Reload», which re-reads `GET /qmp`
 *     and starts the form again from what is served (never the old form with a
 *     fresh token);
 *   · 409 `SCOPE_TRANSFER_REQUIRED` — the sentence and the way to the formal
 *     scope transfer that already exists (the QWP page, `?transfer=1`);
 *   · every other refusal (Executing Party already appointed, a booked
 *     inspection, 422 naming the field, 403/404) as it comes.
 *
 * A NEW package (`qwpId` null, «+ Add a Quality Work Package»): the name and
 * the three positions. It is created with today's door (`createQwp`: the name
 * and the Executing Party or «Not assigned»); with a Verification or an
 * Acceptance Party chosen, the new door follows at once with the token of a
 * FRESH read of the created package. If that second call is refused the
 * package stays created (nothing is deleted) and the editor stays open on it,
 * with the refusal (S3b residue: one server act for the creation).
 *
 * The «⋯» menu removes the package by today's door (`deleteQwp`), with
 * today's confirmation.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  saveQwpStructure, createQwp, deleteQwp, getProjectQmp,
} from '../../lib/api';
import { apiErrorMessage } from '../../pages/TestRegisterPage';

export const NOT_ASSIGNED = '__not_assigned__';
export const NONE = '__none__';
export const NOT_ASSIGNED_LABEL = 'Not assigned';
export const PARTIES_SAVED = 'Parties saved.';

// v83 tokens, dark values (as `RuleEditorPage`).
const H2 = 'text-[20px] leading-[1.3] mb-[7px] font-bold';
const INTRO = 'text-[#afbdd0] max-w-[72ch]';
const PANEL = 'bg-[#101a2b] border border-[#2b3c56] rounded-[10px] p-[18px] my-[16px] max-[580px]:p-[14px]';
const SMALL = 'text-[12px] text-[#afbdd0] font-normal';
const FIELD = 'flex flex-col gap-[6px] font-bold my-[12px]';
const CONTROL = 'w-full min-w-0 max-w-full bg-[#0b1423] text-[#f1f5fb] border border-[#2b3c56] rounded-[7px] '
  + 'p-[11px] min-h-[44px] font-normal disabled:opacity-60 max-[580px]:text-[16px]';
const INFO = 'border-l-[3px] border-[#ff8b32] px-[13px] py-[10px] bg-[#32251e] my-[14px] text-[#f1f5fb] [&_strong]:font-bold';
const PENDING = 'border-l-[3px] border-[#ffd28b] px-[14px] py-[12px] bg-[#332819] my-[12px] text-[#f1f5fb] [&_b]:font-bold';
const BUTTON = 'min-h-[44px] border border-[#2b3c56] rounded-[7px] px-[13px] py-[9px] text-[#f1f5fb] bg-[#0b1423] '
  + 'whitespace-normal disabled:opacity-45 disabled:cursor-not-allowed';
const PRIMARY = 'min-h-[44px] border border-[#ff7a12] rounded-[7px] px-[13px] py-[9px] bg-[#ff7a12] text-[#17202d] '
  + 'font-bold whitespace-normal disabled:opacity-45 disabled:cursor-not-allowed';
const END = 'flex items-center justify-between gap-[12px] flex-wrap pt-[12px] mt-[18px] border-t border-[#2b3c56]';
const ERROR = 'bg-[#332819] text-[#ffa9b2] px-[12px] py-[10px] rounded-[6px] mb-[14px] text-[13px] flex items-center gap-[10px] flex-wrap';
const LINK = 'text-[#ff8b32] underline font-bold';

const HINT_POSITION = 'Choose None if this role is not part of the package’s quality process.';

/** The form of a served `structure[]` row (or the empty form of a new one). */
export function formOf(row) {
  if (!row) return { name: '', ep: NOT_ASSIGNED, vp: NONE, ap: NONE };
  const appointed = row.executing && row.executing.appointed !== false;
  return {
    name: row.name || '',
    ep: appointed ? (row.executing.participation_id || NOT_ASSIGNED) : NOT_ASSIGNED,
    vp: (row.verification && row.verification.participation_id) || NONE,
    ap: (row.acceptance && row.acceptance.participation_id) || NONE,
  };
}

const sameForm = (a, b) => a.name === b.name && a.ep === b.ep && a.vp === b.vp && a.ap === b.ap;
const idOf = (v, empty) => (v === empty ? null : v);

/** The refusal as the editor shows it: the server's words, and its kind. */
export function refusalOf(err) {
  const detail = err && err.response && err.response.data && err.response.data.detail;
  const code = detail && typeof detail === 'object' ? (detail.error || detail.code || null) : null;
  let kind = null;
  if (code === 'QWP_CHANGED') kind = 'changed';
  if (code === 'SCOPE_TRANSFER_REQUIRED') kind = 'scope';
  return { kind, code, message: apiErrorMessage(err, 'Could not save the parties of this package') };
}

function Field({
  label, value, options, onChange, disabled, hint, testId,
}) {
  return (
    <label className={FIELD}>
      {label}
      <select
        className={CONTROL}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {hint && <span className={SMALL}>{hint}</span>}
    </label>
  );
}

export default function PackagePartiesPage({
  projectId, qmp, qwpId, companies, readOnly, onClose, onDirty,
}) {
  const [targetId, setTargetId] = React.useState(qwpId || null);
  const structure = qmp.structure || [];
  const row = targetId ? structure.find((r) => r.qwp_id === targetId) || null : null;
  const [form, setForm] = React.useState(() => formOf(row));
  const [base, setBase] = React.useState(() => formOf(row));
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [menu, setMenu] = React.useState(false);
  const [confirmRemove, setConfirmRemove] = React.useState(false);
  const [reset, setReset] = React.useState(0);

  // «Reload» (QWP_CHANGED): once the plan is read again, the form starts over
  // from what the server holds now.
  React.useEffect(() => {
    if (!reset) return;
    const fresh = formOf(row);
    setForm(fresh);
    setBase(fresh);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  const isNew = !row;
  const dirty = !readOnly && !sameForm(form, base);
  React.useEffect(() => { if (onDirty) onDirty(dirty); }, [dirty, onDirty]);
  React.useEffect(() => () => { if (onDirty) onDirty(false); }, [onDirty]);

  const appointed = Boolean(row && row.executing && row.executing.appointed !== false);
  const companyOptions = () => {
    const opts = (companies || []).map((c) => ({ value: c.id, label: c.name || c.short_name || '' }));
    // A company the package still names but the list no longer has stays readable.
    [row && row.executing && row.executing.appointed !== false ? row.executing : null,
      row && row.verification, row && row.acceptance].forEach((p) => {
      if (p && p.participation_id && !opts.some((o) => o.value === p.participation_id)) {
        opts.push({ value: p.participation_id, label: p.name || '' });
      }
    });
    return opts;
  };
  const epOptions = appointed
    ? companyOptions()
    : [{ value: NOT_ASSIGNED, label: NOT_ASSIGNED_LABEL }, ...companyOptions()];
  const posOptions = [{ value: NONE, label: 'None' }, ...companyOptions()];

  const set = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const finish = async () => {
    await qmp.reload();
    toast.success(PARTIES_SAVED);
    onClose('');
  };

  const saveExisting = async () => {
    await saveQwpStructure(row.qwp_id, {
      executing_party_id: idOf(form.ep, NOT_ASSIGNED),
      verification_company_id: idOf(form.vp, NONE),
      acceptance_company_id: idOf(form.ap, NONE),
      structure_token: row.structure_token,
    });
  };

  const create = async () => {
    const name = form.name.trim();
    let created;
    try {
      const body = form.ep === NOT_ASSIGNED
        ? { name, executing_party_appointed: false }
        : { name, owner_company_id: form.ep };
      const res = await createQwp(projectId, body);
      created = res && res.data;
    } catch (err) {
      setError(refusalOf(err));
      return false;
    }
    if (created && created.id && (form.vp !== NONE || form.ap !== NONE)) {
      try {
        const fresh = await getProjectQmp(projectId);
        const born = ((fresh && fresh.data && fresh.data.structure) || [])
          .find((r) => r.qwp_id === created.id);
        await saveQwpStructure(created.id, {
          executing_party_id: idOf(form.ep, NOT_ASSIGNED),
          verification_company_id: idOf(form.vp, NONE),
          acceptance_company_id: idOf(form.ap, NONE),
          structure_token: born ? born.structure_token : '',
        });
      } catch (err) {
        // The package is created; nothing is deleted. The editor stays open on
        // it, with the refusal, the person's choices kept.
        await qmp.reload();
        const now = { ...formOf(null), name };
        now.ep = form.ep;
        setBase(now);
        setTargetId(created.id);
        setError(refusalOf(err));
        return false;
      }
    }
    return true;
  };

  const onSave = async () => {
    if (readOnly || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        if (await create()) await finish();
      } else {
        await saveExisting();
        await finish();
      }
    } catch (err) {
      setError(refusalOf(err));
    }
    setBusy(false);
  };

  const onReload = async () => {
    setBusy(true);
    await qmp.reload();
    setBusy(false);
    setReset((n) => n + 1);
  };

  const onRemove = async () => {
    setConfirmRemove(false);
    setBusy(true);
    setError(null);
    try {
      await deleteQwp(row.qwp_id);
    } catch (err) {
      setError({ kind: null, message: apiErrorMessage(err, 'Could not remove this package') });
      setBusy(false);
      return;
    }
    await qmp.reload();
    setBusy(false);
    onClose('');
  };

  if (targetId && !row) {
    return (
      <div data-testid="pp-page" data-qwp={targetId}>
        <p className={INTRO} data-testid="pp-missing">This Quality Work Package is no longer in the plan.</p>
        <div className={END} data-testid="pp-footer">
          <button type="button" className={BUTTON} onClick={() => onClose('')} data-testid="pp-cancel">Back</button>
        </div>
      </div>
    );
  }

  const title = isNew ? 'New Quality Work Package' : `${row.name} · parties`;
  const canSave = !readOnly && !busy && !(error && error.kind === 'changed')
    && (!isNew || Boolean(form.name.trim()));

  return (
    <div data-testid="pp-page" data-qwp={targetId || ''}>
      <div className="flex items-start justify-between gap-[12px]">
        <h2 className={H2} data-testid="pp-title">{title}</h2>
        {!isNew && !readOnly && (
          <div className="relative">
            <button
              type="button"
              className={BUTTON}
              aria-label="More actions"
              aria-expanded={menu ? 'true' : 'false'}
              disabled={busy}
              onClick={() => setMenu((m) => !m)}
              data-testid="pp-more"
            >
              ⋯
            </button>
            {menu && (
              <div
                className="absolute right-0 z-[20] mt-[4px] min-w-[260px] bg-[#101a2b] border border-[#2b3c56] rounded-[8px] p-[4px] shadow-[0_12px_34px_rgba(0,0,0,.45)]"
                role="menu"
                data-testid="pp-menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="w-full text-left px-[12px] py-[10px] rounded-[6px] text-[#ffa9b2] hover:bg-[#162238]"
                  onClick={() => { setMenu(false); setConfirmRemove(true); }}
                  data-testid="pp-remove"
                >
                  Remove this Quality Work Package
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <p className={INTRO} data-testid="pp-intro">Responsibilities in this Quality Work Package.</p>

      {confirmRemove && row && (
        <div className={`${INFO} flex items-center gap-[10px] flex-wrap`} data-testid="pp-remove-confirm">
          <span>
            Remove <b>{row.name}</b>?
          </span>
          <button type="button" className={PRIMARY} onClick={onRemove} data-testid="pp-remove-yes">
            Remove
          </button>
          <button type="button" className={BUTTON} onClick={() => setConfirmRemove(false)} data-testid="pp-remove-no">
            Cancel
          </button>
        </div>
      )}

      {error && (
        <div className={ERROR} role="alert" data-testid="pp-error">
          <span data-testid="pp-error-text">{error.message}</span>
          {error.kind === 'changed' && (
            <button type="button" className={BUTTON} disabled={busy} onClick={onReload} data-testid="pp-reload">
              Reload
            </button>
          )}
          {error.kind === 'scope' && targetId && (
            <Link
              to={`/projects/${projectId}/qwps/${targetId}?transfer=1`}
              className={LINK}
              data-testid="pp-scope-transfer"
            >
              Open the scope transfer
            </Link>
          )}
        </div>
      )}

      <section className={PANEL} data-testid="pp-panel">
        <div className={INFO} data-testid="pp-active">
          <strong>Active when saved.</strong>
          {' Saving changes this package’s companies immediately — not only in the draft.'}
        </div>
        {isNew && (
          <label className={FIELD}>
            Quality Work Package
            <input
              className={CONTROL}
              value={form.name}
              placeholder="Package title"
              disabled={busy}
              onChange={(e) => set('name')(e.target.value)}
              data-testid="pp-name"
            />
          </label>
        )}
        <Field
          label="Executing Party — does the work"
          value={form.ep}
          options={epOptions}
          onChange={set('ep')}
          disabled={readOnly || busy}
          hint={appointed ? '' : 'If the company is not appointed yet, choose Not assigned and name it later.'}
          testId="pp-ep"
        />
        <Field
          label="Verification Party — checks the work"
          value={form.vp}
          options={posOptions}
          onChange={set('vp')}
          disabled={readOnly || busy}
          hint={HINT_POSITION}
          testId="pp-vp"
        />
        <Field
          label="Acceptance Party — formally accepts the work"
          value={form.ap}
          options={posOptions}
          onChange={set('ap')}
          disabled={readOnly || busy}
          hint={HINT_POSITION}
          testId="pp-ap"
        />
        <p className={SMALL}>
          Without Verification and Acceptance Parties, the package is managed as internal QA by the Executing Party.
        </p>
      </section>
      <div className={PENDING} data-testid="pp-pending">
        Rules already saved for this package are never rewritten when its parties change. A saved rule that names a
        party the package no longer has is listed under <b>Review &amp; publish</b>.
      </div>
      <div className={END} data-testid="pp-footer">
        <button type="button" className={BUTTON} onClick={() => onClose('')} data-testid="pp-cancel">
          {readOnly ? 'Back' : 'Cancel'}
        </button>
        <span className="flex-1 text-[12px] text-[#afbdd0] max-[580px]:basis-full" />
        {!readOnly && (
          <button type="button" className={PRIMARY} disabled={!canSave} onClick={onSave} data-testid="pp-save">
            Save parties
          </button>
        )}
      </div>
    </div>
  );
}
